/**
 * Quantum Design System Web Components
 * 
 * This is the main entry point for all components
 * which registers them as custom elements.
 */

declare const __QDS_PACKAGE_VERSION__: string;
declare const __QDS_BUILD_NUMBER__: string;
declare const __QDS_BUILD_ID__: string;
declare const __QDS_SOURCE_BRANCH__: string;
declare const __QDS_SOURCE_VERSION__: string;
declare const __QDS_GENERATED_AT_UTC__: string;

// Import all components
import './help-button/qds-help-button.js';
import './feedback-button/qds-feedback-button.js';
import './icon-button/qds-icon-button.js';
import './theme-switch/qds-theme-switch.js';
import './gradient-line/qds-gradient-line.js';
import './hero/qds-hero.js';
import './company-logo/qds-company-logo.js';
import './user-avatar/qds-user-avatar.js';
import './color-helper/qds-color-helper.js';
import './color-picker/qds-color-picker.js';
import './font-picker/qds-font-picker.js';
import './icon-picker/qds-icon-picker.js';
import './image-helper/qds-image-helper.js';
import './code-block/qds-code-block.js';
import './menu-spacer/qds-menu-spacer.js';
import './settings-button/qds-settings-button.js';
import './toast/qds-toast.js';
import './notification-tray/qds-notification-tray.js';
import './main-toolbar/qds-main-toolbar.js';
import './hierarchical-menu/qds-hierarchical-menu.js';
import './sidebar-menu/qds-sidebar-menu.js';
import './horizontal-menu/qds-horizontal-menu.js';
import './page-frame/qds-page-frame.js';
import './diagram-host/qds-diagram-host.js';
// Import tree view component
import './tree-view/qds-tree-view.js';
// Import diagram manager component
import './diagram-manager/qds-diagram-manager.js';
// Import the property editor to ensure it's registered
import './property-editor/qds-property-editor.js';
import './content-renderer/qds-content-renderer.js';
import './content-designer/qds-content-designer.js';
import './content-palette/qds-content-palette.js';
import './content-outline/qds-content-outline.js';
import './content-canvas/qds-content-canvas.js';
import './content-properties/qds-content-properties.js';
import './content-block-frame/qds-content-block-frame.js';
import './card-grid-editor/qds-card-grid-editor.js';
import './action-panel/qds-action-panel.js';
import './action-panel-items-editor/qds-action-panel-items-editor.js';
import './inline-text-editor/qds-inline-text-editor.js';
import './inline-rich-text/qds-inline-rich-text.js';
import './rich-text-editor/qds-rich-text-editor.js';
// Import card components
import './card-basic/qds-card-basic.js';
import './card-advanced/qds-card-advanced.js';
// Import Power BI embed component
import './powerbi-embed/qds-powerbi-embed.js';
// Import form & table primitives
import './field/qds-field.js';
import './text-input/qds-text-input.js';
import './numeric-input/qds-numeric-input.js';
import './select/qds-select.js';
import './checkbox/qds-checkbox.js';
import './radio-group/qds-radio-group.js';
import './date-picker/qds-date-picker.js';
import './form-grid/qds-form-grid.js';
import './empty-state/qds-empty-state.js';
import './column-header/qds-column-header.js';
import './row-actions/qds-row-actions.js';
import './pagination/qds-pagination.js';
import './data-table/qds-data-table.js';
import './chart/qds-chart.js';
import './stacked-timeline/qds-stacked-timeline.js';

// Export components for direct import
export { QdsIcon } from './icon/qds-icon.js';
export { QdsBreadcrumb } from './breadcrumb/qds-breadcrumb.js';
export type { BreadcrumbItem } from './breadcrumb/qds-breadcrumb.js';
export { QdsCard } from './card/qds-card.js';
export { QdsButtonGroup } from './button-group/qds-button-group.js';
export { QdsToggleButton } from './toggle-button/qds-toggle-button.js';
export { QdsSegmentedControl } from './segmented-control/qds-segmented-control.js';
export { QdsMenu } from './menu/qds-menu.js';
export { QdsSplitButton } from './split-button/qds-split-button.js';
export { QdsDrawer } from './drawer/qds-drawer.js';
export { QdsStepper } from './stepper/qds-stepper.js';
export type { StepperStep } from './stepper/qds-stepper.js';
export { QdsCheckboxGroup } from './checkbox-group/qds-checkbox-group.js';
export { QdsListbox } from './listbox/qds-listbox.js';
export { QdsCombobox } from './combobox/qds-combobox.js';
export { QdsFileUpload } from './file-upload/qds-file-upload.js';
export { QdsSlider } from './slider/qds-slider.js';
export { QdsInputGroup } from './input-group/qds-input-group.js';
export { QdsErrorSummary } from './error-summary/qds-error-summary.js';
export type { ErrorSummaryItem } from './error-summary/qds-error-summary.js';
export type { MenuItem, MenuEntry } from './menu/qds-menu.js';
export { QdsSeparator } from './separator/qds-separator.js';
export { QdsButton } from './button/qds-button.js';
export { QdsTextarea } from './textarea/qds-textarea.js';
export { QdsSwitch } from './switch/qds-switch.js';
export { QdsDialog } from './dialog/qds-dialog.js';
export { confirmDialog } from './dialog/confirm-dialog.js';
export type { ConfirmDialogOptions } from './dialog/confirm-dialog.js';
export { QdsPopover } from './popover/qds-popover.js';
export { QdsTooltip } from './tooltip/qds-tooltip.js';
export { QdsTabs } from './tabs/qds-tabs.js';
export { QdsTab } from './tabs/qds-tab.js';
export { QdsTabPanel } from './tabs/qds-tab-panel.js';
export { QdsDisclosure } from './disclosure/qds-disclosure.js';
export { QdsAccordion } from './accordion/qds-accordion.js';
export { QdsMessage } from './message/qds-message.js';
export { QdsSpinner } from './spinner/qds-spinner.js';
export { QdsProgress } from './progress/qds-progress.js';
export { QdsSkeleton } from './skeleton/qds-skeleton.js';
export { QdsBadge } from './badge/qds-badge.js';
export { QdsTag } from './tag/qds-tag.js';
export { QdsChart } from './chart/qds-chart.js';
export { QdsStackedTimeline } from './stacked-timeline/qds-stacked-timeline.js';
export { chartSeriesColor } from './chart/chart-colors.js';
export type { QdsChartSeries, QdsChartTooltip } from './chart/chart-colors.js';
export type { QdsTimelinePeriod, QdsTimelineRange } from './stacked-timeline/timeline-layout.js';
export { default as PropertyEditor } from './property-editor/qds-property-editor.js';
export { default as QdsContentRenderer } from './content-renderer/qds-content-renderer.js';
export { default as QdsContentDesigner } from './content-designer/qds-content-designer.js';
export { default as QdsContentPalette } from './content-palette/qds-content-palette.js';
export { default as QdsContentOutline } from './content-outline/qds-content-outline.js';
export { default as QdsContentCanvas } from './content-canvas/qds-content-canvas.js';
export { default as QdsContentProperties } from './content-properties/qds-content-properties.js';
export { default as QdsContentBlockFrame } from './content-block-frame/qds-content-block-frame.js';
export { default as QdsCardGridEditor } from './card-grid-editor/qds-card-grid-editor.js';
export { default as QdsActionPanel } from './action-panel/qds-action-panel.js';
export { default as QdsActionPanelItemsEditor } from './action-panel-items-editor/qds-action-panel-items-editor.js';
export { default as QdsInlineTextEditor } from './inline-text-editor/qds-inline-text-editor.js';
export { default as QdsInlineRichText } from './inline-rich-text/qds-inline-rich-text.js';
export { default as QdsRichTextEditor } from './rich-text-editor/qds-rich-text-editor.js';
export {
  QdsContentRegistry,
  createBuiltInBlockDefinitions,
  createBlockInstance,
  cloneContentDocument,
  normalizeContentDocument
} from './content-blocks/qds-content-blocks.js';
export { default as CardBasic } from './card-basic/qds-card-basic.js';
export { default as CardAdvanced } from './card-advanced/qds-card-advanced.js';
export { default as QdsHero } from './hero/qds-hero.js';
export { default as QdsColorPicker } from './color-picker/qds-color-picker.js';
export { default as QdsFontPicker } from './font-picker/qds-font-picker.js';
export { default as QdsIconPicker } from './icon-picker/qds-icon-picker.js';
export { default as PowerBIEmbed } from './powerbi-embed/qds-powerbi-embed.js';
export { QdsTreeView } from './tree-view/qds-tree-view.js';
export { QdsDiagramManager } from './diagram-manager/qds-diagram-manager.js';

// Export theme manager for applications to use
export { default as themeManager } from './shared/theme-manager.js';

export const qdsBuildInfo = Object.freeze({
  packageVersion: __QDS_PACKAGE_VERSION__,
  buildNumber: __QDS_BUILD_NUMBER__,
  buildId: __QDS_BUILD_ID__,
  sourceBranch: __QDS_SOURCE_BRANCH__,
  sourceVersion: __QDS_SOURCE_VERSION__,
  generatedAtUtc: __QDS_GENERATED_AT_UTC__
});

// Export providers for tree view
export type { ITreeDataProvider } from './providers/ITreeDataProvider.js';
export { AzureStorageTreeProvider } from './providers/AzureStorageTreeProvider.js';
export { TreeDataProviderError, TreeDataProviderErrorCode } from './providers/TreeDataProviderError.js';
export type { AzureStorageTreeProviderConfig } from './providers/AzureStorageTreeProvider.js';

const sourceSuffix = qdsBuildInfo.sourceVersion ? ` @ ${qdsBuildInfo.sourceVersion.slice(0, 7)}` : '';
console.info(`[QDS] Loaded build ${qdsBuildInfo.buildNumber} (package ${qdsBuildInfo.packageVersion})${sourceSuffix}`);

export * from './time-picker/qds-time-picker.js';
export * from './date-time-picker/qds-date-time-picker.js';
export * from './period-picker/qds-period-picker.js';
